import {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { get, post } from '@/utils';
import { useAuthContext } from '@/providers';
import { AuthState } from '@/providers/auth';
import { useHasPermission, P } from '@/components';
import { CHAT_ROOM_TYPE, ChatConversationSummary, ChatRoom } from '@/types/chat';
import { isNeighborhoodModerator } from '@/providers/auth/neighborhood-moderation';
import { CHAT_ROOM_READ_EVENT } from './events';
import { loadAllChatConversationSummaries } from './api';

export { CHAT_ROOM_READ_EVENT } from './events';

// ==============================|| CHAT NOTIFICATIONS (global, pt. bell-ul din header) ||============================== //
// Provider app-wide, MONTAT peste toată aplicația (nu doar pe pagina de chat). Face poll pe
// api/chat/rooms + api/chat/notifications (unread + ultim mesaj) și expune camerele cu necitite
// pentru bell-ul din header — echivalentul notifications provider din geoPulse (aici doar poll,
// fără WS: badge-ul nu are nevoie de instant). Rulează DOAR pt utilizatorii cu permisiunea `chat`.

const POLL_MS = 20000;

export interface ChatNotificationEntry {
	roomId: number;
	conversationId: string;
	roomTypeId: number;
	title: string;
	isArchived: boolean;
	isMuted: boolean; // notificări oprite (areNotificationsMuted) — exclus din badge/feed clopoțel
	imageUrl: string | null; // avatar cameră (proiect sau administrație) pt. avatar; null → iconiță de tip
	unreadCount: number;
	preview: string;
	lastActivity: string;
}

interface ChatNotificationsContextValue {
	roomCatalogRevision: number;
	summaryCatalogRevision: number;
	catalogRooms: ChatRoom[];
	catalogSummaries: ChatConversationSummary[];
	items: ChatNotificationEntry[]; // doar camerele cu necitite (feed Notificări)
	allEntries: ChatNotificationEntry[]; // toate camerele, sortate pe activitate (widget Mesaje)
	unreadTotal: number;
	loading: boolean;
	refresh: () => Promise<void>;
	markAllRead: () => Promise<void>;
	markRoomRead: (roomId: number) => void; // zeroează local unread-ul unei camere (optimist)
}

const ChatNotificationsContext = createContext<ChatNotificationsContextValue>({
	roomCatalogRevision: 0,
	summaryCatalogRevision: 0,
	catalogRooms: [],
	catalogSummaries: [],
	items: [],
	allEntries: [],
	unreadTotal: 0,
	loading: false,
	refresh: async () => {},
	markAllRead: async () => {},
	markRoomRead: () => {}
});

export const useChatNotifications = (): ChatNotificationsContextValue =>
	useContext(ChatNotificationsContext);

const roomTitle = (r: ChatRoom): string => {
	const t = r.title?.trim();
	if (t) return t;
	return r.roomTypeId === CHAT_ROOM_TYPE.Project
		? `Proiect #${r.projectId ?? r.id}`
		: `Administrație #${r.administrationId}`;
};

const previewOf = (s?: ChatConversationSummary): string => {
	const text = s?.lastMessage?.payload?.text;
	if (typeof text === 'string' && text) return text;
	if (s?.lastMessage?.attachment) return '📎 Atașament';
	return '';
};

export const ChatNotificationsProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { authState, sessionExpired, user } = useAuthContext();
	const canChat = useHasPermission(P.chat);
	// moderatorul de cartier nu are chatul general (doar tabul cartierului) — nimic de urmărit
	const enabled =
		authState === AuthState.SignedIn &&
		!sessionExpired &&
		canChat &&
		!isNeighborhoodModerator(user);

	const [rooms, setRooms] = useState<ChatRoom[]>([]);
	const [conversations, setConversations] = useState<
		Record<string, ChatConversationSummary>
	>({});
	const [loading, setLoading] = useState(false);
	const [roomCatalogRevision, setRoomCatalogRevision] = useState(0);
	const [summaryCatalogRevision, setSummaryCatalogRevision] = useState(0);
	const roomsSignatureRef = useRef('');
	const conversationsSignatureRef = useRef('');
	const refreshGenerationRef = useRef(0);
	const hasLoadedRef = useRef(false);

	const roomsRef = useRef(rooms);
	useEffect(() => {
		roomsRef.current = rooms;
	}, [rooms]);

	// zeroează local unread-ul unei camere (la click pe notificare + la event de la pagina de chat)
	const markRoomRead = useCallback((roomId: number) => {
		const room = roomsRef.current.find((r) => r.id === roomId);
		if (!room) return;
		setConversations((prev) =>
			prev[room.conversationId]?.unreadCount
				? {
						...prev,
						[room.conversationId]: { ...prev[room.conversationId], unreadCount: 0 }
					}
				: prev
		);
	}, []);

	// sync cu markRead din pagina de chat (deschidere cameră din rail) → scade badge-ul instant
	useEffect(() => {
		const handler = (e: Event) => {
			const { detail } = e as CustomEvent<{ roomId?: number }>;
			if (typeof detail?.roomId === 'number') markRoomRead(detail.roomId);
		};
		window.addEventListener(CHAT_ROOM_READ_EVENT, handler);
		return () => window.removeEventListener(CHAT_ROOM_READ_EVENT, handler);
	}, [markRoomRead]);

	const refresh = useCallback(async () => {
		if (!enabled) return;
		const generation = ++refreshGenerationRef.current;
		const showLoading = !hasLoadedRef.current;
		if (showLoading) setLoading(true);
		try {
			const [roomsRes, notificationSummaries] = await Promise.all([
				get('api/chat/rooms').catch(() => null),
				loadAllChatConversationSummaries(get).catch(() => null)
			]);
			if (generation !== refreshGenerationRef.current) return;
			if (roomsRes) {
				const payload = roomsRes.data;
				const nextRooms: ChatRoom[] | null = Array.isArray(payload)
					? payload
					: Array.isArray(payload?.items)
						? payload.items
						: null;
				if (nextRooms) {
					const signature = JSON.stringify(nextRooms);
					if (signature !== roomsSignatureRef.current) {
						roomsSignatureRef.current = signature;
						setRooms(nextRooms);
					}
					// This is a freshness marker, not only a change counter. A successful room
					// response can safely supersede an older authorization snapshot even when
					// its serialized contents happen to be unchanged.
					setRoomCatalogRevision((revision) => revision + 1);
				}
			}
			if (notificationSummaries) {
					const map: Record<string, ChatConversationSummary> = {};
					notificationSummaries.forEach((c) => {
						map[c.id] = c;
					});
					const signature = JSON.stringify(map);
					if (signature !== conversationsSignatureRef.current) {
						conversationsSignatureRef.current = signature;
						setConversations(map);
					}
					setSummaryCatalogRevision((revision) => revision + 1);
			}
			hasLoadedRef.current = true;
		} finally {
			if (showLoading && generation === refreshGenerationRef.current) setLoading(false);
		}
	}, [enabled]);

	// poll cât timp userul e logat + are permisiune de chat; altfel golim starea și nu apelăm
	useEffect(() => {
		if (!enabled) {
			refreshGenerationRef.current += 1;
			hasLoadedRef.current = false;
			roomsSignatureRef.current = '';
			conversationsSignatureRef.current = '';
			setRooms([]);
			setConversations({});
			setRoomCatalogRevision(0);
			setSummaryCatalogRevision(0);
			setLoading(false);
			return undefined;
		}
		refresh();
		const id = window.setInterval(() => refresh(), POLL_MS);
		return () => window.clearInterval(id);
	}, [enabled, refresh]);

	const allEntries = useMemo<ChatNotificationEntry[]>(
		() =>
			rooms
				.map((r) => {
					const s = conversations[r.conversationId];
					return {
						roomId: r.id,
						conversationId: r.conversationId,
						roomTypeId: r.roomTypeId,
						title: roomTitle(r),
						isArchived: r.isArchived,
						isMuted: r.areNotificationsMuted,
						imageUrl: r.avatar?.publicUrl ?? null,
						unreadCount: s?.unreadCount ?? 0,
						preview: previewOf(s),
						lastActivity: s?.updatedAt || s?.lastMessage?.createdAt || r.createdAt
					};
				})
				.sort((a, b) => b.lastActivity.localeCompare(a.lastActivity)),
		[rooms, conversations]
	);
	// feed-ul clopoțelului + badge-ul din header NU includ camerele cu notificări oprite (mute).
	// Rămân vizibile în drawer-ul Mesaje (allEntries), doar de-emphasizate.
	const items = useMemo(
		() => allEntries.filter((e) => e.unreadCount > 0 && !e.isMuted),
		[allEntries]
	);

	const unreadTotal = useMemo(
		() => items.reduce((sum, e) => sum + e.unreadCount, 0),
		[items]
	);

	const markAllRead = useCallback(async () => {
		const targets = rooms.filter(
			(r) => (conversations[r.conversationId]?.unreadCount ?? 0) > 0
		);
		await Promise.all(
			targets.map((r) => {
				const s = conversations[r.conversationId];
				return s?.lastMessage
					? post(`api/chat/rooms/${r.id}/read`, { lastReadSeq: s.lastMessage.seq }).catch(
							() => undefined
						)
					: Promise.resolve(undefined);
			})
		);
		setConversations((prev) => {
			const next = { ...prev };
			targets.forEach((r) => {
				if (next[r.conversationId])
					next[r.conversationId] = { ...next[r.conversationId], unreadCount: 0 };
			});
			return next;
		});
	}, [rooms, conversations]);

	const value = useMemo<ChatNotificationsContextValue>(
		() => ({
			roomCatalogRevision,
			summaryCatalogRevision,
			catalogRooms: rooms,
			catalogSummaries: Object.values(conversations),
			items,
			allEntries,
			unreadTotal,
			loading,
			refresh,
			markAllRead,
			markRoomRead
		}),
		[
			roomCatalogRevision,
			summaryCatalogRevision,
			rooms,
			conversations,
			items,
			allEntries,
			unreadTotal,
			loading,
			refresh,
			markAllRead,
			markRoomRead
		]
	);

	return (
		<ChatNotificationsContext.Provider value={value}>
			{children}
		</ChatNotificationsContext.Provider>
	);
};
